import React from 'react';
import MaterialTable, { MTableGroupRow } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Chip, Box, Typography, Select, MenuItem, FormControl, IconButton, Menu, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Divider } from '@material-ui/core';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EmailIcon from '@material-ui/icons/Email';
import EditIcon from '@material-ui/icons/Edit';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import { CircularProgress, Snackbar } from '@material-ui/core';
import RunParticipantDialog from './RunParticipantDialog';

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '-');

const typeColors = { adult: '#2196f3', child: '#ff9800', employee: '#9c27b0' };
const statusColors = { registered: '#ff9800', confirmed: '#41924E', cancelled: '#f44336' };
const racePackColors = { not_taken: '#ff9800', taken: '#41924E' };
const racePackLabels = { not_taken: 'Not Taken', taken: 'Taken' };

const chipStyle = (color) => ({
  backgroundColor: color || '#757575',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.8rem',
  height: '26px',
});

export default class RunParticipantTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      columns: [
        {
          title: 'Participant',
          field: 'full_name',
          grouping: false,
          render: rowData => (
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.3 }}>
                {rowData.full_name}
              </Typography>
              <Typography variant="caption" component="div" style={{ color: '#888', fontSize: '0.85rem', lineHeight: 1.3 }}>
                BIB: {rowData.bib_name}{rowData.bib_number ? ` / ${rowData.bib_number}` : ''}
              </Typography>
              {rowData.external_bib_number && (
                <Typography variant="caption" component="div" style={{ color: '#888', fontSize: '0.8rem', lineHeight: 1.3 }}>
                  Ext. BIB: {rowData.external_bib_number}
                </Typography>
              )}
            </Box>
          ),
        },
        {
          title: 'QR Code',
          field: 'qr_code',
          grouping: false,
          render: rowData => {
            const qr = rowData.qr_code;
            if (!qr) return '-';
            return (
              <Typography
                variant="body2"
                title={qr}
                style={{ fontFamily: 'monospace', fontSize: '0.92rem', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {qr}
              </Typography>
            );
          },
        },
        {
          title: 'Category',
          field: 'category_name',
          cellStyle: { fontSize: '0.95rem' },
        },
        {
          title: 'Type',
          field: 'participant_type',
          width: 100,
          lookup: { adult: 'Adult', child: 'Child', employee: 'Employee' },
          render: rowData => {
            const type = rowData.participant_type;
            return <Chip label={capitalize(type)} size="small" style={chipStyle(typeColors[type])} />;
          },
        },
        {
          title: 'Jersey',
          field: 'jersey_size',
          width: 80,
          grouping: false,
          cellStyle: { fontSize: '0.95rem' },
        },
        {
          title: 'Gender',
          field: 'gender',
          width: 100,
          cellStyle: { fontSize: '0.95rem' },
          lookup: { male: 'Male', female: 'Female' },
          render: rowData => capitalize(rowData.gender),
        },
        {
          title: 'Status',
          field: 'status',
          width: 110,
          lookup: { registered: 'Registered', confirmed: 'Confirmed', cancelled: 'Cancelled' },
          render: rowData => {
            const status = rowData.status;
            return <Chip label={capitalize(status)} size="small" style={chipStyle(statusColors[status])} />;
          },
        },
        {
          title: 'Race Pack',
          field: 'race_pack_status',
          width: 110,
          lookup: { not_taken: 'Not Taken', taken: 'Taken' },
          render: rowData => {
            const rp = rowData.race_pack_status;
            return <Chip label={racePackLabels[rp] || capitalize(rp)} size="small" style={chipStyle(racePackColors[rp] || '#757575')} />;
          },
        },
        {
          title: 'Taken At',
          field: 'race_pack_taken_at',
          grouping: false,
          cellStyle: { fontSize: '0.85rem' },
          render: rowData => rowData.race_pack_taken_at || '-',
        },
        {
          title: 'Referral',
          field: 'referral_name',
          grouping: false,
          cellStyle: { fontSize: '0.95rem' },
          render: rowData => rowData.referral_name || '-',
        },
        {
          title: 'Transaction ID',
          field: 'purchase_id',
          grouping: false,
          width: 90,
          cellStyle: { fontSize: '0.85rem', fontFamily: 'monospace' },
          render: rowData => rowData.purchase_id || '-',
        },
        {
          title: 'Transaction Code',
          field: 'purchase_ref',
          grouping: false,
          cellStyle: { fontSize: '0.85rem', fontFamily: 'monospace' },
          render: rowData => {
            const ref = rowData.purchase_ref;
            if (!ref) return '-';
            return (
              <Typography
                variant="body2"
                title={ref}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {ref}
              </Typography>
            );
          },
        },
        {
          title: 'Registered',
          field: 'created_at',
          grouping: false,
          cellStyle: { fontSize: '0.95rem' },
          defaultSort: 'desc',
        },
      ],
      statusFilter: props.category_filter ? 'all' : 'confirmed',
      racePackFilter: 'all',
      categoryFilter: props.category_filter || '',
      dialogOpen: false,
      selectedParticipant: null,
      exportMenuAnchor: null,
      actionMenuAnchor: null,
      actionMenuParticipant: null,
      resendSnackbar: false,
      resendMessage: '',
      resending: false,
      editDialogOpen: false,
      editParticipant: null,
      editExternalBib: '',
      editSaving: false,
      givingRacepack: false,
    };

    this.tableRef = React.createRef();
  }

  componentDidMount() {
    this._handleRefresh = () => {
      if (this.tableRef.current) this.tableRef.current.onQueryChange();
    };
    document.addEventListener('race-pack-table-refresh', this._handleRefresh);
  }

  componentWillUnmount() {
    document.removeEventListener('race-pack-table-refresh', this._handleRefresh);
  }

  handleRowClick = (_event, rowData) => {
    this.setState({
      dialogOpen: true,
      selectedParticipant: rowData,
    });
  };

  handleCloseDialog = () => {
    this.setState({
      dialogOpen: false,
      selectedParticipant: null,
    });
  };

  handleStatusFilterChange = (event) => {
    this.setState({ statusFilter: event.target.value }, () => {
      this.tableRef.current && this.tableRef.current.onQueryChange();
    });
  };

  handleRacePackFilterChange = (event) => {
    this.setState({ racePackFilter: event.target.value }, () => {
      this.tableRef.current && this.tableRef.current.onQueryChange();
    });
  };

  handleExportMenuOpen = (event) => {
    this.setState({ exportMenuAnchor: event.currentTarget });
  };

  handleExportMenuClose = () => {
    this.setState({ exportMenuAnchor: null });
  };

  handleExportCsv = (status, racePackStatus) => {
    this.setState({ exportMenuAnchor: null });
    let url = this.props.download_url + '?status=' + encodeURIComponent(status);
    if (racePackStatus) {
      url += '&race_pack_status=' + encodeURIComponent(racePackStatus);
    }
    if (this.state.categoryFilter) {
      url += '&category_id=' + encodeURIComponent(this.state.categoryFilter);
    }
    window.location.href = url;
  };

  handleActionMenuOpen = (event, rowData) => {
    event.stopPropagation();
    this.setState({ actionMenuAnchor: event.currentTarget, actionMenuParticipant: rowData });
  };

  handleActionMenuClose = () => {
    this.setState({ actionMenuAnchor: null, actionMenuParticipant: null });
  };

  handleResendConfirmation = () => {
    const participant = this.state.actionMenuParticipant;
    if (!participant) return;

    this.setState({ actionMenuAnchor: null, resending: true });

    const participantId = participant.id;
    const apiUrl = this.props.api_url;
    const baseUrl = apiUrl.split('/api/')[0];
    const herminaRunSlug = apiUrl.match(/hermina-runs\/([^/]+)/)?.[1];
    const url = baseUrl + '/hermina-runs/' + herminaRunSlug + '/participants/' + participantId + '/resend_confirmation';
    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token ? token.getAttribute('content') : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        this.setState({
          resending: false,
          resendSnackbar: true,
          resendMessage: data.message || 'Email sent successfully',
        });
      })
      .catch(() => {
        this.setState({
          resending: false,
          resendSnackbar: true,
          resendMessage: 'Failed to send email',
        });
      });
  };

  handleCloseSnackbar = () => {
    this.setState({ resendSnackbar: false, resendMessage: '' });
  };

  handleEditOpen = (participant) => {
    this.setState({
      actionMenuAnchor: null,
      editDialogOpen: true,
      editParticipant: participant,
      editExternalBib: participant.external_bib_number || '',
    });
  };

  handleEditClose = () => {
    this.setState({ editDialogOpen: false, editParticipant: null, editExternalBib: '' });
  };

  handleEditSave = () => {
    const participant = this.state.editParticipant;
    if (!participant) return;

    this.setState({ editSaving: true });

    const apiUrl = this.props.api_url;
    const baseUrl = apiUrl.split('/api/')[0];
    const herminaRunSlug = apiUrl.match(/hermina-runs\/([^/]+)/)?.[1];
    const url = baseUrl + '/hermina-runs/' + herminaRunSlug + '/participants/' + participant.id;
    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': token ? token.getAttribute('content') : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ hermina_run_participant: { external_bib_number: this.state.editExternalBib } }),
    })
      .then(response => {
        if (response.ok) {
          this.setState({
            editSaving: false,
            editDialogOpen: false,
            editParticipant: null,
            editExternalBib: '',
            resendSnackbar: true,
            resendMessage: 'External BIB number updated successfully',
          });
          if (this.tableRef.current) this.tableRef.current.onQueryChange();
        } else {
          throw new Error('Update failed');
        }
      })
      .catch(() => {
        this.setState({
          editSaving: false,
          resendSnackbar: true,
          resendMessage: 'Failed to update external BIB number',
        });
      });
  };

  handleGiveRacepackFromDialog = (participant, onDone) => {
    const apiUrl = this.props.api_url;
    const baseUrl = apiUrl.split('/api/')[0];
    const herminaRunSlug = apiUrl.match(/hermina-runs\/([^/]+)/)?.[1];
    const url = baseUrl + '/hermina-runs/' + herminaRunSlug + '/participants/' + participant.id + '/update_race_pack';
    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': token ? token.getAttribute('content') : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        if (onDone) onDone();
        this.setState({
          dialogOpen: false,
          selectedParticipant: null,
          resendSnackbar: true,
          resendMessage: data.message || 'Race pack given successfully',
        });
        if (this.tableRef.current) this.tableRef.current.onQueryChange();
        document.dispatchEvent(new Event('race-pack-table-refresh'));
      })
      .catch(() => {
        if (onDone) onDone();
        this.setState({
          resendSnackbar: true,
          resendMessage: 'Failed to give race pack',
        });
      });
  };

  handleGiveRacepack = () => {
    const participant = this.state.actionMenuParticipant;
    if (!participant) return;

    this.setState({ actionMenuAnchor: null, givingRacepack: true });

    const apiUrl = this.props.api_url;
    const baseUrl = apiUrl.split('/api/')[0];
    const herminaRunSlug = apiUrl.match(/hermina-runs\/([^/]+)/)?.[1];
    const url = baseUrl + '/hermina-runs/' + herminaRunSlug + '/participants/' + participant.id + '/update_race_pack';
    const token = document.querySelector('meta[name="csrf-token"]');

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': token ? token.getAttribute('content') : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        this.setState({
          givingRacepack: false,
          resendSnackbar: true,
          resendMessage: data.message || 'Race pack given successfully',
        });
        if (this.tableRef.current) this.tableRef.current.onQueryChange();
        document.dispatchEvent(new Event('race-pack-table-refresh'));
      })
      .catch(() => {
        this.setState({
          givingRacepack: false,
          resendSnackbar: true,
          resendMessage: 'Failed to give race pack',
        });
      });
  };

  getTheme = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';

    return createTheme({
      palette: {
        type: isDarkMode ? 'dark' : 'light',
        primary: { main: '#7cb342' },
        secondary: { main: '#41924E' },
        background: {
          paper: isDarkMode ? '#2a3441' : '#ffffff',
          default: isDarkMode ? '#1e2530' : '#f5f6fa',
        },
        text: {
          primary: isDarkMode ? '#e8eaed' : '#2d3436',
          secondary: isDarkMode ? '#9aa0a6' : '#636e72',
        },
      },
    });
  };

  getTableStyles = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';

    return {
      headerStyle: {
        position: 'sticky',
        top: 0,
        backgroundColor: isDarkMode ? '#323d4d' : '#fafafa',
        color: isDarkMode ? '#e8eaed' : '#555',
        fontWeight: 700,
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
        padding: '12px 16px',
        borderBottomColor: isDarkMode ? '#3d4a5c' : '#e0e0e0',
        zIndex: 1,
      },
      rowStyle: {
        backgroundColor: isDarkMode ? '#2a3441' : '#fff',
        color: isDarkMode ? '#e8eaed' : '#2d3436',
        whiteSpace: 'nowrap',
      },
      cellStyle: {
        padding: '10px 16px',
        fontSize: '0.95rem',
      },
      searchFieldStyle: {
        backgroundColor: isDarkMode ? '#323d4d' : '#fff',
        color: isDarkMode ? '#e8eaed' : '#2d3436',
      },
    };
  };

  render() {
    const tableStyles = this.getTableStyles();

    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <MaterialTable
          title={
            <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
              <Typography variant="h5" style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                {this.props.title || 'Participants'}
              </Typography>
              <FormControl size="small" variant="outlined">
                <Select
                  value={this.state.statusFilter}
                  onChange={this.handleStatusFilterChange}
                  style={{ minWidth: 140, fontSize: '0.9rem' }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="registered">Registered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              {!this.props.race_pack_status && (
                <FormControl size="small" variant="outlined">
                  <Select
                    value={this.state.racePackFilter}
                    onChange={this.handleRacePackFilterChange}
                    style={{ minWidth: 140, fontSize: '0.9rem' }}
                  >
                    <MenuItem value="all">All Race Pack</MenuItem>
                    <MenuItem value="taken">Taken</MenuItem>
                    <MenuItem value="not_taken">Not Taken</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          }
          tableRef={this.tableRef}
          columns={this.state.columns}
          data={query =>
            new Promise((resolve) => {
              let url = this.props.api_url;
              url += (url.includes('?') ? '&' : '?');
              url += 'per_page=' + query.pageSize;
              url += '&q=' + encodeURIComponent(query.search || '');
              url += '&page=' + (query.page + 1);
              url += '&status=' + encodeURIComponent(this.state.statusFilter);
              if (this.props.race_pack_status) {
                url += '&race_pack_status=' + encodeURIComponent(this.props.race_pack_status);
              } else if (this.state.racePackFilter && this.state.racePackFilter !== 'all') {
                url += '&race_pack_status=' + encodeURIComponent(this.state.racePackFilter);
              }
              if (this.state.categoryFilter) {
                url += '&category_id=' + encodeURIComponent(this.state.categoryFilter);
              }
              if (query.orderBy) {
                url += '&sort_by=' + encodeURIComponent(query.orderBy.field);
                url += '&sort_dir=' + (query.orderDirection || 'asc');
              }
              fetch(url)
                .then(response => response.json())
                .then(result => {
                  const data = result.participants.data.map(item => ({
                    ...item,
                    ...item.attributes,
                  }));
                  resolve({
                    data: data,
                    page: result.pagination.page - 1,
                    totalCount: result.pagination.count,
                  });
                })
                .catch(error => {
                  console.error('Error fetching participants:', error);
                  resolve({ data: [], page: 0, totalCount: 0 });
                });
            })
          }
          components={{
            GroupRow: props => {
              const col = props.groupData.column;
              const rawValue = props.groupData.value;
              const displayValue = col && col.lookup && col.lookup[rawValue]
                ? col.lookup[rawValue]
                : (rawValue || '-');
              const customProps = {
                ...props,
                groupData: { ...props.groupData, value: displayValue },
              };
              return <MTableGroupRow {...customProps} />;
            },
          }}
          actions={[
            {
              icon: () => <SaveAltIcon />,
              tooltip: 'Export CSV',
              isFreeAction: true,
              onClick: (event) => this.handleExportMenuOpen(event),
            },
            {
              icon: 'refresh',
              tooltip: 'Refresh Data',
              isFreeAction: true,
              onClick: () => this.tableRef.current && this.tableRef.current.onQueryChange(),
            },
            rowData => ({
              icon: () => <MoreVertIcon fontSize="small" />,
              tooltip: 'Actions',
              onClick: (event) => this.handleActionMenuOpen(event, rowData),
            }),
          ]}
          onRowClick={this.handleRowClick}
          options={{
            pageSize: 10,
            pageSizeOptions: [5, 10, 20, 50],
            exportButton: false,
            sorting: true,
            search: true,
            grouping: true,
            tableLayout: 'auto',
            padding: 'default',
            headerStyle: tableStyles.headerStyle,
            cellStyle: tableStyles.cellStyle,
            rowStyle: {
              ...tableStyles.rowStyle,
              cursor: 'pointer',
            },
            searchFieldStyle: tableStyles.searchFieldStyle,
            maxBodyHeight: 'calc(100vh - 280px)',
          }}
          style={{
            padding: '16px',
          }}
        />

        <Menu
          anchorEl={this.state.exportMenuAnchor}
          open={Boolean(this.state.exportMenuAnchor)}
          onClose={this.handleExportMenuClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ style: { minWidth: 200 } }}
        >
          <MenuItem onClick={() => this.handleExportCsv('all')}>
            <ListItemIcon style={{ minWidth: 36 }}><PeopleOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="All Statuses" />
          </MenuItem>
          <MenuItem onClick={() => this.handleExportCsv('confirmed')}>
            <ListItemIcon style={{ minWidth: 36 }}><CheckCircleOutlineIcon fontSize="small" style={{ color: '#41924E' }} /></ListItemIcon>
            <ListItemText primary="Confirmed" />
          </MenuItem>
          <MenuItem onClick={() => this.handleExportCsv('registered')}>
            <ListItemIcon style={{ minWidth: 36 }}><ScheduleIcon fontSize="small" style={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText primary="Registered" />
          </MenuItem>
          <MenuItem onClick={() => this.handleExportCsv('cancelled')}>
            <ListItemIcon style={{ minWidth: 36 }}><CancelOutlinedIcon fontSize="small" style={{ color: '#f44336' }} /></ListItemIcon>
            <ListItemText primary="Cancelled" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => this.handleExportCsv('all', 'taken')}>
            <ListItemIcon style={{ minWidth: 36 }}><CardGiftcardIcon fontSize="small" style={{ color: '#41924E' }} /></ListItemIcon>
            <ListItemText primary="Race Pack Taken" />
          </MenuItem>
          <MenuItem onClick={() => this.handleExportCsv('all', 'not_taken')}>
            <ListItemIcon style={{ minWidth: 36 }}><CardGiftcardIcon fontSize="small" style={{ color: '#ff9800' }} /></ListItemIcon>
            <ListItemText primary="Race Pack Not Taken" />
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={this.state.actionMenuAnchor}
          open={Boolean(this.state.actionMenuAnchor)}
          onClose={this.handleActionMenuClose}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {this.state.actionMenuParticipant && this.state.actionMenuParticipant.race_pack_status === 'not_taken' && (
            <MenuItem onClick={this.handleGiveRacepack} disabled={this.state.givingRacepack}>
              <ListItemIcon style={{ minWidth: 36 }}>
                {this.state.givingRacepack ? <CircularProgress size={18} /> : <CardGiftcardIcon fontSize="small" style={{ color: '#41924E' }} />}
              </ListItemIcon>
              <ListItemText primary="Give Racepack" />
            </MenuItem>
          )}
          <MenuItem onClick={() => this.handleEditOpen(this.state.actionMenuParticipant)}>
            <ListItemIcon style={{ minWidth: 36 }}>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit External BIB" />
          </MenuItem>
          <MenuItem onClick={this.handleResendConfirmation} disabled={this.state.resending}>
            <ListItemIcon style={{ minWidth: 36 }}>
              {this.state.resending ? <CircularProgress size={18} /> : <EmailIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary="Resend Confirmation Email" />
          </MenuItem>
        </Menu>

        <Snackbar
          open={this.state.resendSnackbar}
          autoHideDuration={4000}
          onClose={this.handleCloseSnackbar}
          message={this.state.resendMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />

        <Dialog
          open={this.state.editDialogOpen}
          onClose={this.handleEditClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Edit External BIB Number</DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 12, color: '#888' }}>
              {this.state.editParticipant ? this.state.editParticipant.full_name : ''}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="External BIB Number"
              variant="outlined"
              size="small"
              value={this.state.editExternalBib}
              onChange={(e) => this.setState({ editExternalBib: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleEditClose} color="default" size="small">
              Cancel
            </Button>
            <Button
              onClick={this.handleEditSave}
              color="primary"
              variant="contained"
              size="small"
              disabled={this.state.editSaving}
            >
              {this.state.editSaving ? <CircularProgress size={18} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <RunParticipantDialog
          open={this.state.dialogOpen}
          onClose={this.handleCloseDialog}
          participant={this.state.selectedParticipant}
          onGiveRacepack={this.handleGiveRacepackFromDialog}
        />
      </MuiThemeProvider>
    );
  }
}
